import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import cx from 'classnames';
import { get, debounce } from 'core/libs/lodash';
import { compose } from 'core/libs/recompose';
import { withRouter } from 'core/libs/router';

import withTheme from 'core/hocs/withTheme';
import withTopic from 'core/hocs/withTopic';
import withBreakpoint from 'core/hocs/withBreakpoint';
import { SWIPER_MOUNTED } from 'core/site/constants';
import { Desktop } from 'core/components/breakpoint';
import EmptyWrapper from 'core/components/EmptyWrapper';
import ThemeSwitch from 'core/components/ThemeSwitch';
import Image from 'core/components/Image';
import Button from 'core/components/Button';
import Swipe, { Slide } from 'core/components/Swipe';
import Link from 'core/components/Link';
import SmoothHeightChanger from 'core/components/SmoothHeightChanger';

import modelPropTypes, {
  photoGalleryAttributes,
} from 'core/utils/prop-types/model';
import breakpointPropTypes from 'core/utils/prop-types/breakpoint';
import themePropTypes from 'core/utils/prop-types/theme';
import { scrollToElement, updateBodyAttributes } from 'core/utils/dom';

import { isImageModel } from 'core/utils/content';

import { resolveScopedStyles } from 'core/utils/styled-jsx';

import GalleryHeader from './Header';
import ImageInfo from './ImageInfo';
import Arrows from './Arrows';
import Navigation from './Navigation';
import AdPlaceholder from './AdPlaceholder';
import Counter from './Counter';

import {
  getViewDimensionsState,
  getInjectAds,
  getGalleryAspectRatio,
} from './utils';
import styles from './index.styl';

const LEFT_ARROW_KEY = 37;
const RIGHT_ARROW_KEY = 39;
const ESC_KEY = 27;
const EMPTY = {};
const NOOP = () => EMPTY;
const MIN_Y_DIFF = 100;
const MAX_IMAGE_HEIGHT = 600;

const injectAdsPropTypes = PropTypes.shape({
  component: PropTypes.func,
  count: PropTypes.number,
});

const loadedFullVersions = new Set();

class Gallery extends PureComponent {
  static propTypes = {
    /**
     * Опциональный компонент, который в случае передачи будет отображатся
     * последним слайдом и может содержать любую информацию.
     *
     * Важно чтоб этот компонент подстраивался под высоту контейнера и никак
     * на нее не пытался влиять.
     */
    additionalSlide: PropTypes.func,
    /** Включает hash навигацию */
    hashNavigation: PropTypes.bool,
    /**
     * Url страницы на которой находится галерея для хеш навигации.
     *
     * При бесконечном скролле ссылка на топик в котором находится галерея
     * может отличаться от location в роутере, поэтому передаем галерее
     * точную ссылку
     */
    hashNavigationLink: PropTypes.string,
    /**
     * Баннер, который нужно вставлять между слайдами.
     *
     * component - компонент
     * count - вставлять баннер через каждые count слайдов
     */
    injectAds: PropTypes.shape({
      desktop: injectAdsPropTypes,
      mobile: injectAdsPropTypes,
    }),
    /**
     * Банеры, которые нужно перевызывать при пролистывании галереи.
     *
     * TODO: Не очень хорошо завязываться на имя банера, поэтому можно
     * компоненту Ad добавить пропс token, по которому можно будет явно
     * идентифицировать баннер.
     */
    adsToReload: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        count: PropTypes.number,
      })
    ),
    /** Скрывает кнопку сбрасылвания галереи на первую картинку */
    disableReloadButton: PropTypes.bool,
    /**
     * Включает "бесконечную прокрутку".
     * На последней картинке по клику на стрелку вперед галерея переключаеться на первую
     * На первой картинке по клику на стрелку назад галерея переключаеться на последнюю
     */
    isInfinite: PropTypes.bool,
    /** Данные для фотогалереи */
    photoGallery: modelPropTypes(photoGalleryAttributes),
    /**
     * Если проставлено то информация о картинке ограничена шириной топика
     */
    infoWithSideIndent: PropTypes.bool,
    /**
     * Колличество пикселей на которые сдвинуты стрелки в десктоп(не фуллскрин)
     * режиме от краев галереи
     */
    arrowsShift: PropTypes.number,
    /**
     * Коллбек, который будет вызван в конце componentDidMount.
     * В качестве аргумента ему передаётся инстанс галереи.
     */
    onMount: PropTypes.func,
    /**
     * Коллбек, который будет вызван в конце componentDidUpdate.
     * Первым аргументом ему передаётся инстанс галереи, затем
     * аргументы метода componentDidUpdate.
     */
    onUpdate: PropTypes.func,
    /** Версия картинок которые будут использоватся (original, main_gallery) */
    imageVersionName: PropTypes.string,
    /**
     * Показывать иконку, позволяющую скачивать изображения.
     */
    showDownloadIcon: PropTypes.bool,

    /**
     * Функционал сворачивания длинных подписей с возможностью
     * раскрытия по нажатию.
     */
    withSmoothHeightChange: PropTypes.bool,

    /**
     * Приоритет загрузки для первого изображения в галерее
     */
    firstImageFetchPriority: PropTypes.string,
    /**
     * Параметр используеться исключительно для zvuk.ru.
     * Когда он проставлен картинка по своим габаритам точно соотвествеут контейнеру.
     * Это позволяет например задать для нее закругленные углы.
     */
    imageSizeAsContainer: PropTypes.bool,
    /**
     * Название темы, в которой должна отображаться подпись,
     * расположенная под галереей.
     *
     * Имеет смысл использовать этот проп
     * только когда галерея завёрнута в тему, отличающуюся от остальной
     * страницы.
     */
    infoUnderThemeName: PropTypes.string,
    /*
    * Пропсы для captionCredits
    */
    captionCreditsProps: PropTypes.object,
    /** @ignore */
    theme: themePropTypes(`
      {
        controls: {
          gallery: {
            slide: {
              background,
              borderRadius
            },
            fullscreen: {
              background,
            },
          },
        },
      }
    `),
    /** @ignore */
    location: PropTypes.object,
    /** @ignore */
    history: PropTypes.object,
    /** @ignore */
    breakpoint: breakpointPropTypes(),
    /** @ignore */
    isMobile: PropTypes.bool,
    /** @ignore */
    isDesktop: PropTypes.bool,
    /** @ignore */
    topic: PropTypes.shape({
      infinityIndex: PropTypes.number,
    }),
  };

  static contextTypes = {
    webdavHost: PropTypes.string,
  };

  static defaultProps = {
    imageVersionName: 'original',
  };

  constructor(props) {
    super(props);

    this.initedActiveItem = 0;

    const {
      hashNavigation,
      location,
      topic,
    } = props;
    const { infinityIndex } = topic || {};

    if (hashNavigation && !infinityIndex) {
      const activeItem = (+location.hash.slice(1) || 1) - 1;
      this.initedActiveItem = activeItem;
    }

    this.state = {
      changeIndex: 0,
      viewWidth: 0,
      viewHeight: 0,
      activeIndex: 0,
      realActiveIndex: 0,
      isFullscreen: false,
      touchMoveOpacity: 1,
      imageInnerShift: 0,
      isExitingFullscreen: false,
      ...this.getSlidesWithInjectedAds(),
    };
  }

  componentDidMount() {
    const {
      hashNavigation,
      location,
      topic,
      onMount,
    } = this.props;

    const { infinityIndex } = topic || {};

    this.goToHashedSlide = PubSub.subscribe(SWIPER_MOUNTED, () => {
      if (hashNavigation && !infinityIndex) {
        const activeItem = (+location.hash.slice(1) || 1) - 1;
        this.gotoImage(activeItem);
      }
      PubSub.unsubscribe(this.goToHashedSlide);
    });

    this.mounted = true;
    window.addEventListener('resize', this.updateWindowDimensions);
    if (typeof onMount === 'function') {
      onMount(this);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const injectAdsChanged = !!getInjectAds(prevProps) !== !!getInjectAds(this.props);
    const photoGalleryChanged = !!this.props.photoGallery && !!prevProps.photoGallery && this.props.photoGallery.id !== prevProps.photoGallery.id;

    if (injectAdsChanged || photoGalleryChanged) {
      this.setState({ ...this.getSlidesWithInjectedAds(), isFullscreen: false }); // eslint-disable-line
      updateBodyAttributes();
    }
    if (typeof this.props.onUpdate === 'function') {
      this.props.onUpdate(this, prevProps, prevState, snapshot);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    this.unsubscribe();
    this.mounted = false;
    updateBodyAttributes();
  }

  touchStartX = null;
  touchEndX = null;
  touchStartY = null;
  touchEndY = null;
  xDiff = null;
  yDiff = null;
  waitingForFrame = false;

  handleTouchStart = (e) => {
    const touch = e.targetTouches[0];

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  };

  handleTouchMoveRafCallback = touch => {
    this.waitingForFrame = false;
    this.touchEndX = touch.clientX;
    this.touchEndY = touch.clientY;
    this.xDiff = this.touchStartX - this.touchEndX;
    this.yDiff = this.touchStartY - this.touchEndY;
    if (this.yDiff > 0) {
      this.setState({ imageInnerShift: this.yDiff });
      if (this.state.touchMoveOpacity === 1) {
        this.setState({ touchMoveOpacity: 0.5 });
      }
    }
  };

  handleTouchMove = ({ targetTouches }) => {
    if (targetTouches.length !== 1) return;

    if (!this.waitingForFrame) {
      this.waitingForFrame = true;
      window.requestAnimationFrame(() => this.handleTouchMoveRafCallback(targetTouches[0]));
    }
  };

  handleTouchEnd = () => {
    if (this.yDiff > MIN_Y_DIFF && Math.abs(this.xDiff) < Math.abs(this.yDiff)) {
      this.toggleFullScreen();
    }
    this.yDiff = null;
    this.xDiff = null;
    this.skipFullscreenImageState();
  };

  handleTouchCancel = () => {
    this.skipFullscreenImageState();
  };

  skipFullscreenImageState = () => {
    window.requestAnimationFrame(() => this.setState({ touchMoveOpacity: 1, imageInnerShift: 0 }));
  };

  getSlidesWithInjectedAds = () => {
    const {
      photoGallery,
      imageVersionName,
      theme: {
        layout: {
          contentColumnMaxWidth,
        },
      },
    } = this.props;
    const gotPhotoGallery = !!(photoGallery && photoGallery.attributes.images.length);

    if (!gotPhotoGallery) {
      return {
        totalCount: 0,
        images: [],
        height: 0,
        width: 0,
        ratio: 1,
      };
    }

    let images = photoGallery.attributes.images;
    let totalCount = photoGallery.attributes.image_count;

    this.unsubscribe();

    const injectAds = getInjectAds(this.props);

    if (injectAds) {
      const { component: Ad } = injectAds;

      const slides = images.reduce((p, c, i) =>
        (i + 1) % injectAds.count === 0
          ? p.concat(c).concat(<Ad placeholder={AdPlaceholder} />)
          : p.concat(c)
      , []);

      images = slides;
      totalCount = slides.length;

      this.subscribe();
    }

    const firstImageRequiredCover = images[0].attributes.versions[imageVersionName];
    if (!firstImageRequiredCover) {
      console.warn(`[Gallery] '${imageVersionName}' image cover is not available, falling back to original image`);
    }
    const firstImageCover = firstImageRequiredCover || images[0].attributes.versions.original;
    const {
      height,
      width,
    } = firstImageCover;
    return {
      totalCount,
      images,
      height: height > MAX_IMAGE_HEIGHT ? MAX_IMAGE_HEIGHT : height,
      width: width > parseFloat(contentColumnMaxWidth) ? parseFloat(contentColumnMaxWidth) : width,
      ratio: height / width,
    };
  };

  updateWindowDimensions = debounce(() => {
    this.setState(getViewDimensionsState());
  }, 500);

  subscribe = () => {
    this.eventSub = PubSub.subscribe('slide.remove', (topic, data) => {
      if (!this.container) return;
      const banner = this.container.querySelector(`#${data.domId}`);
      if (banner) {
        const slideEL = banner.closest(`.${this.heroSwipe.getSlideClassName()}`);
        const slidesEL = this.heroSwipe.getSlides();
        const index = Array.from(slidesEL).indexOf(slideEL);
        this.removeSlide(index);
      }
    });
  };

  unsubscribe = () => {
    this.eventSub && PubSub.unsubscribe(this.eventSub);
  };

  prev = () => {
    const {
      isInfinite,
      additionalSlide,
    } = this.props;

    const {
      images,
      activeIndex,
    } = this.state;

    const slidesLength = images.length + !!additionalSlide;

    if (isInfinite && activeIndex === 0) {
      this.heroSwipe.goto(slidesLength - 1);
      return;
    }
    this.heroSwipe.prev();
  };

  next = () => {
    const {
      isInfinite,
      additionalSlide,
    } = this.props;

    const {
      images,
      activeIndex,
    } = this.state;

    const slidesLength = images.length + !!additionalSlide;

    if (isInfinite && activeIndex === (slidesLength - 1)) {
      this.heroSwipe.goto(0);
      return;
    }
    this.heroSwipe.next();
  };

  goto = (index, activeIndex) => {
    const { images } = this.state;
    let newIndex = index;
    const diff = index - activeIndex;

    if (Math.abs(diff) === 2) {
      const adIndex = diff > 0 ? index - 1 : index + 1;
      if (!isImageModel(images[adIndex])) {
        newIndex = adIndex;
      }
    }
    this.heroSwipe.goto(newIndex);
  };

  gotoImage = index => {
    const { photoGallery } = this.props;
    const { images } = this.state;
    const imgs = photoGallery.attributes.images;

    let imageIndex;

    if (index >= imgs.length) { // additionalSlide
      imageIndex = images.length;
    } else {
      imageIndex = images.findIndex(item => item.id === imgs[index].id);
    }

    this.goto(imageIndex);
  };

  scrollToGallery = () => scrollToElement(this.container, 1 / 3);

  focusGallery = () => this.container.focus({
    preventScroll: true,
  });

  removeSlide = index => {
    const { images, activeIndex } = this.state;
    images.splice(index, 1);
    this.setState({ images, totalCount: images.length }, () => {
      if (activeIndex > index) {
        this.goto(index);
      }
    });
  };

  handleKeyboard = ({ keyCode }) => {
    const isLeftKey = keyCode === LEFT_ARROW_KEY;
    const isRightKey = keyCode === RIGHT_ARROW_KEY;

    if (isLeftKey || isRightKey) {
      this.scrollToGallery();
    }

    isLeftKey && this.heroSwipe.prev();
    isRightKey && this.heroSwipe.next();
    this.state.isFullscreen && keyCode === ESC_KEY && this.toggleFullScreen();
  };

  onActiveIndexChange = ({ index }) => {
    const {
      activeIndex,
      changeIndex,
      realActiveIndex,
      images,
      isFullscreen,
    } = this.state;

    const {
      photoGallery,
      hashNavigation,
      adsToReload,
    } = this.props;

    if (index === activeIndex) return;
    const imgs = photoGallery.attributes.images;

    const activeImageIndex = index === images.length
      ? imgs.length
      : imgs.findIndex(item => item.id === images[index].id);

    const newRealActiveIndex = activeImageIndex !== -1
      ? activeImageIndex
      : realActiveIndex;

    const newChangeIndex = changeIndex + 1;

    if (this.mounted && hashNavigation) {
      const {
        location: {
          pathname,
          search,
        },
        history,
        hashNavigationLink,
      } = this.props;

      history.replace({
        pathname: hashNavigationLink || pathname,
        search,
        hash: String(newRealActiveIndex + 1),
        state: { infinity: true },
      });
    }

    const nextSlide = images[index + 1];
    const prevSlide = images[index - 1];

    if (nextSlide && !isImageModel(nextSlide)) {
      this.initInjectedAd(index + 1);
    }

    if (prevSlide && !isImageModel(prevSlide)) {
      this.initInjectedAd(index - 1);
    }

    if (!isFullscreen && adsToReload && adsToReload.length) {
      adsToReload.forEach(({ name, count }) => {
        if (newChangeIndex % count === 0) {
          PubSub.publish('reload.block_if_visible', { name });
        }
      });
    }

    if (isFullscreen) {
      loadedFullVersions.add(index);
    }

    this.setState({
      activeIndex: index,
      changeIndex: newChangeIndex,
      realActiveIndex: newRealActiveIndex,
    });
  };

  initInjectedAd = (index) => {
    const adWrapper = this.heroSwipe.getSlides()[index].querySelector('[data-capirs]');
    if (!adWrapper) return;

    const paramString = adWrapper.getAttribute('data-capirs');
    const { domId } = JSON.parse(paramString || '{}');

    PubSub.publish('init.block', { domId });
  };

  toggleFullScreen = () => {
    this.setState(({ isFullscreen, imageInnerShift }) => {
      return {
        isFullscreen: this.props.photoGallery.type === 'photoGallery',
        ...!isFullscreen && imageInnerShift !== 0 && {
          touchMoveOpacity: 1,
          imageInnerShift: 0,
        },
        ...isFullscreen && {
          isExitingFullscreen: true,
        },
        ...getViewDimensionsState(),
      };
    }, () => {
      if (this.state.isExitingFullscreen) {
        setTimeout(() => {
          this.setState({
            isFullscreen: false,
            isExitingFullscreen: false,
          }, () => {
            updateBodyAttributes({ toFullscreen: this.state.isFullscreen });
          });
        }, 200);
      } else {
        updateBodyAttributes({ toFullscreen: this.state.isFullscreen });
      }
    });
  };

  render() {
    const {
      photoGallery,
      additionalSlide: AdditionalSlide,
      disableReloadButton,
      isInfinite,
      infoWithSideIndent,
      imageVersionName,
      arrowsShift,
      isMobile,
      isDesktop,
      showDownloadIcon,
      withSmoothHeightChange,
      firstImageFetchPriority,
      imageSizeAsContainer,
      infoUnderThemeName,
      theme: {
        controls: {
          gallery,
        },
      },
      captionCreditsProps,
    } = this.props;

    const {
      webdavHost,
    } = this.context;

    const {
      activeIndex,
      realActiveIndex,
      viewWidth,
      viewHeight,
      // Данные о галерее готовые к отрисовке, вместе с рекламными слайдами.
      images: gallerySlides,
      // Количество всех слайдов, вместе с рекламными
      totalCount,
      height: slideHeight,
      width,
      isFullscreen,
      isExitingFullscreen,
    } = this.state;

    const isAdditionalSlideActive = (!!AdditionalSlide && activeIndex === totalCount);
    const isGalleryOfTopics = photoGallery.type === 'galleryOfTopics';
    const showImageInfo = !isAdditionalSlideActive
      || isImageModel(gallerySlides[activeIndex]);
    const useSmoothInfoHeightChange = withSmoothHeightChange && !isFullscreen;

    const desktopHeight = isFullscreen ? viewHeight : slideHeight;
    const desktopWidth = isFullscreen ? viewWidth : width;
    let maxSlideWidth = 0;
    const mobileGalleryAspectRatio = isMobile && getGalleryAspectRatio(photoGallery.attributes.images);
    // Количество реальных слайдов(без рекламы). Будем отображать в счетчике.
    const imagesCount = photoGallery.attributes.image_count + Number(!!AdditionalSlide);
    const {
      attributes,
    } = (gallerySlides[Math.min(activeIndex, totalCount - 1)] || EMPTY);

    const {
      caption,
      credits,
      alt,
    } = attributes || EMPTY;

    const InfoWrapper = useSmoothInfoHeightChange ? SmoothHeightChanger : EmptyWrapper;
    const InfoThemer = isFullscreen || infoUnderThemeName ? ThemeSwitch : EmptyWrapper;

    const scope = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .fixedSlide
            max-height ${slideHeight}px
            :global(.mobile) &
              min-height 250px
        `}</style>
      </scope>
    );
    return (
      <div
        key={photoGallery?.id}
        tabIndex='0'
        className={cx(
          'gallery', // используется в Arrows
          styles.gallery,
          isFullscreen && styles._fullscreen,
          isExitingFullscreen && styles._exiting,
          isGalleryOfTopics && styles._isGalleryOfTopics,
        )}
        onKeyDown={this.handleKeyboard}
        onClick={this.scrollToGallery}
        ref={node => (this.container = node)}
      >
        <style jsx>{`
          .${styles.gallery}
            &.${styles._fullscreen}
              background ${gallery.fullscreen.background}
          .${styles.imageInner}
            .${styles._fullscreen} &
              opacity ${this.state.touchMoveOpacity}
              transform translateY(calc(-50% - ${this.state.imageInnerShift}px))
            .${styles.imageInnerWrapper}
              border-radius ${gallery.slide.borderRadius}px
          .${styles.imageContainer}
            background ${isFullscreen ? 'transparent' : gallery.slide.background}
        `}</style>
        <div
          className={styles.imageContainer}
          {...isMobile && isFullscreen && {
            onTouchStart: this.handleTouchStart,
            onTouchMove: this.handleTouchMove,
            onTouchEnd: this.handleTouchEnd,
            onTouchCancel: this.handleTouchCancel,
          }}
        >
          <div className={styles.imageInner}>
            <Swipe
              ref={instance => (this.heroSwipe = instance)}
              onInteraction={this.focusGallery}
              onActiveIndexChange={this.onActiveIndexChange}
              {...isGalleryOfTopics && isDesktop && { containerClass: styles.topicsSwipeContainer }}
            >
              {gallerySlides.map((image, i) => {
                if (!isImageModel(image)) {
                  return (
                    <Slide width='100%' key={`ad_slide_${i}`}>
                      <div className={cx(styles.image, styles.imageAd)}>
                        {image}
                        <div className={styles.imageAdButton}>
                          {(gallerySlides.length > activeIndex + 1) && (
                            <Button
                              onClick={() => this.goto(activeIndex + 1)}
                              {...isMobile && { size: 'small' }}
                            >
                              Листай дальше, еще много интересного
                            </Button>
                          )}
                        </div>
                      </div>
                    </Slide>
                  );
                }

                const {
                  id: imageId,
                  attributes: {
                    versions: {
                      original: originalCover,
                      [imageVersionName]: customCover,
                      thumbnail: previewCover,
                    },
                  },
                  topicUrl,
                } = image;
                const cover = customCover || originalCover;

                const desktopRule = (cover.height / cover.width < 1 && !isGalleryOfTopics)
                  ? {
                    height: Math[imageSizeAsContainer ? 'min' : 'max']((cover.height / cover.width) * desktopWidth, desktopHeight),
                  } : {
                    width: Math.min((cover.width / cover.height) * desktopHeight, desktopWidth),
                  };
                const desktopImageParams = desktopRule.width
                  ? {
                    width: desktopRule.width,
                    height: Math.min((cover.height / cover.width) * desktopRule.width, desktopHeight),
                  } : {
                    height: desktopRule.height,
                    width: Math[imageSizeAsContainer ? 'min' : 'max']((cover.width / cover.height) * desktopRule.height, desktopWidth),
                  };

                const imageParams = isMobile
                  ? {
                    aspectRatio: isFullscreen ? cover.width / cover.height : mobileGalleryAspectRatio,
                    ...(viewWidth > viewHeight) && { maxHeight: viewHeight },
                    ...!isGalleryOfTopics && { maxHeight: slideHeight },
                  } : {
                    ...desktopImageParams,
                    ...isFullscreen && { maxHeight: viewHeight, maxWidth: cover.width },
                  };

                maxSlideWidth = maxSlideWidth < imageParams.width ? imageParams.width : maxSlideWidth;
                const slideOpacity = !isGalleryOfTopics || (isGalleryOfTopics && activeIndex === i) ? '1' : '.5';
                const imageComponentProps = {
                  className: styles.image,
                  style: { opacity: slideOpacity },
                };
                const imageComponent = (
                  <Image
                    placement={isGalleryOfTopics ? 'cover' : 'contain'}
                    previewSrc={previewCover}
                    alt={alt}
                    {...imageParams}
                    {...isFullscreen || loadedFullVersions.has(activeIndex)
                      ? {
                        url: cover.url || (webdavHost || '') + cover.rel_url,
                      }
                      : {
                        src: cover,
                      }}
                    {...this.initedActiveItem === i && {
                      fetchPriority: firstImageFetchPriority,
                      instant: firstImageFetchPriority === 'high',
                    }}
                  />
                );

                return (
                  <Slide
                    width='100%'
                    key={imageId}
                    additionalClass={cx(
                      !isFullscreen && scope.wrapClassNames('fixedSlide'),
                      isGalleryOfTopics && isDesktop && styles.slideIndent,
                      'imageSlideWrapper' // Todo: используется на сберАвто, переделать закругления у изображений
                    )}
                  >
                    {isGalleryOfTopics ? (
                      <Link
                        to={topicUrl}
                        {...imageComponentProps}
                      >
                        <div
                          className={cx(
                            isDesktop && !isFullscreen && styles.imageInnerWrapper,
                            isMobile && isGalleryOfTopics && styles.imageInnerWrapperTopics
                          )}
                        >
                          {imageComponent}
                        </div>
                      </Link>
                    ) : (
                      <div
                        onClick={isFullscreen ? NOOP : this.toggleFullScreen}
                        {...imageComponentProps}
                      >
                        {imageComponent}
                      </div>
                    )}
                  </Slide>
                );
              }).concat(!!AdditionalSlide && (
                <Slide
                  width='100%'
                  key={imagesCount}
                  additionalClass={cx(
                    !isFullscreen && scope.wrapClassNames('fixedSlide'),
                    styles.additionalSlide
                  )}
                >
                  <div className={cx(styles.image, styles.additionalSlideImage)}>
                    <AdditionalSlide
                      photoGallery={photoGallery}
                      isFullscreen={isFullscreen}
                    />
                  </div>
                </Slide>
              ))}
            </Swipe>
          </div>

          <Desktop>
            <Arrows
              {...{
                hasExtraSlide: Boolean(AdditionalSlide),
                activeIndex: activeIndex,
                totalCount,
                isFullscreen,
                isInfinite,
                prevHandler: this.prev,
                nextHandler: this.next,
                slideWidth: maxSlideWidth,
                arrowsShift: isGalleryOfTopics ? -35 : arrowsShift,
              }}
            />
          </Desktop>

          {isFullscreen && (
            <Counter
              activeIndex={realActiveIndex}
              totalIndex={imagesCount}
            />
          )}

          <GalleryHeader
            downloadSrc={get(gallerySlides[activeIndex], 'attributes.versions.original.rel_url')}
            showDownloadIcon={showDownloadIcon}
            isFullscreen={isFullscreen}
            toggleFullscreenHandler={this.toggleFullScreen}
          />
        </div>

        {!isFullscreen && (
          <div className={styles.navigationWrapper}>
            <Navigation
              images={gallerySlides}
              activeIndex={activeIndex}
              focusHandler={this.focusGallery}
              gotoHandler={this.goto}
              disableReloadButton={disableReloadButton}
              imagesCount={imagesCount}
            />
          </div>
        )}

        <InfoWrapper
          updateHeightKey={credits + isAdditionalSlideActive}
          collapsedHeight={isMobile ? 155 : 205}
        >
          {showImageInfo &&
            <InfoThemer name={isFullscreen ? 'dark' : infoUnderThemeName}>
              <ImageInfo
                {...{
                  caption,
                  credits,
                  isFullscreen,
                  isInContent: isGalleryOfTopics,
                  withSideIndents: infoWithSideIndent,
                  ...isGalleryOfTopics && { totalCount: gallerySlides[activeIndex].topicGalleryCount },
                  ...captionCreditsProps,
                }}
              />
            </InfoThemer>
          }
        </InfoWrapper>

        <scope.styles />
      </div>
    );
  }
}

const GalleryWithHOCs = compose(
  withTopic,
  withRouter,
  withBreakpoint,
  withTheme,
)(Gallery);

GalleryWithHOCs.displayName = 'Gallery';

export { Gallery as StorybookComponent };
export default GalleryWithHOCs;
